import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { chatApi } from '../api/chat';
import BackLink from '../components/BackLink';
import FranchiseeProfile from '../components/FranchiseeProfile';
import SatelliteFallbackBoundary from '../components/SatelliteFallbackBoundary';
import Tabs from '../components/Tabs';
import { useAuth } from '../hooks/useAuth';
import AdMatchTab from '../tabs/AdMatchTab';
import CallsAndLeadsTab from '../tabs/CallsAndLeadsTab';
import ContentTab from '../tabs/ContentTab';
import ChatTab from '../tabs/ChatTab';
import ImportantLinksTab from '../tabs/ImportantLinksTab';
import PaidPerformanceTab from '../tabs/PaidPerformanceTab';
import PerformanceTab from '../tabs/PerformanceTab';
import SocialTab from '../tabs/SocialTab';
import WebsiteTab from '../tabs/WebsiteTab';
import WelcomeHomeTab from '../tabs/WelcomeHomeTab';
import { useFranchiseesQuery } from '../api/queries';
import type { TabId } from '../types';
import { TABS } from '../types';

type Props = {
  af: string;
  initialTab?: TabId;
  onChangeTab: (tab: TabId) => void;
  /** When provided, renders a "← Back to {backHomeLabel}" link above the
   *  franchisee profile. Omitted for franchisee-role users who have no
   *  parent context to return to. */
  onBackHome?: () => void;
  backHomeLabel?: string;
};

function isReady(tab: TabId): boolean {
  return TABS.find((t) => t.id === tab)?.ready ?? false;
}

/** Hiding the button is not enough — ?tab=tab-chat in the URL would still
 *  render the panel. This is the same gate the tab strip uses, applied to what
 *  actually renders.
 *
 *  Chat is the one tab whose visibility is not role alone: admins always see
 *  it, and a franchisee sees it only once we have switched chat on for their
 *  AF. `chatOn` is undefined while that is still loading, which reads as "not
 *  yet" — the tab appears when the answer arrives rather than flashing up and
 *  disappearing. */
function tabAllowed(tab: TabId, role: string | undefined, chatOn?: boolean): boolean {
  const def = TABS.find((t) => t.id === tab);
  if (tab === 'tab-chat') return role === 'admin' || chatOn === true;
  if (!def?.roles) return true;
  return !!role && (def.roles as string[]).includes(role);
}

export default function FranchiseeDetail({
  af,
  initialTab,
  onChangeTab,
  onBackHome,
  backHomeLabel,
}: Props) {
  const [tab, setTab] = useState<TabId>(initialTab ?? 'tab-website');
  const { user } = useAuth();
  // Admins always see the Chat tab, so only a non-admin needs this asked.
  // Failures resolve to "off": a franchisee should never get the tab because
  // a lookup errored.
  const { data: chatEnabled } = useQuery({
    queryKey: ['chat-enabled', af],
    queryFn: () => chatApi.isEnabled(af).catch(() => ({ enabled: false })),
    enabled: Boolean(af) && user?.role !== 'admin',
    staleTime: 5 * 60 * 1000,
  });
  const chatOn = user?.role === 'admin' ? true : chatEnabled?.enabled;
  const { data: franchisees } = useFranchiseesQuery();
  const primaryAf = af.replace(/\.[0-9]+$/, '');

  useEffect(() => {
    if (initialTab && initialTab !== tab) setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  // A deep link to a tab this role cannot see lands on the default tab rather
  // than a blank panel.
  useEffect(() => {
    if (!user) return;
    // Chat's answer arrives a moment after the page does. Bouncing while it is
    // still undefined would throw a franchisee off their own ?tab=tab-chat
    // deep link a beat before we learn they were allowed on it.
    if (tab === 'tab-chat' && chatOn === undefined) return;
    if (!tabAllowed(tab, user.role, chatOn)) setTab('tab-website');
  }, [user, tab, chatOn]);

  function handleTab(t: TabId) {
    setTab(t);
    onChangeTab(t);
  }

  return (
    <SatelliteFallbackBoundary
      af={af}
      primaryAf={primaryAf}
      render={(effectiveAf, fellBack) => {
        const selected = franchisees?.find((f) => f.af_number === effectiveAf) ?? null;
        return (
          <>
            {onBackHome && backHomeLabel && (
              <BackLink label={backHomeLabel} onClick={onBackHome} />
            )}
            {fellBack && (
              <div className="satellite-fallback-banner">
                Showing data for primary location <strong>{primaryAf}</strong> — the
                detail view for satellite <strong>{af}</strong> couldn't be displayed.
              </div>
            )}
            {selected && <FranchiseeProfile franchisee={selected} />}
            <Tabs active={tab} onChange={handleTab} chatOn={chatOn} />
            <main className="tab-content">
              {tab === 'tab-website' && <WebsiteTab franchisee={selected} />}
              {tab === 'tab-performance' && <PerformanceTab franchisee={selected} />}
              {tab === 'tab-paid' && <PaidPerformanceTab franchisee={selected} />}
              {tab === 'tab-calls' && <CallsAndLeadsTab af={effectiveAf} />}
              {tab === 'tab-admatch' && <AdMatchTab af={effectiveAf} />}
              {tab === 'tab-social' && <SocialTab af={effectiveAf} />}
              {tab === 'tab-welcomehome' && <WelcomeHomeTab af={effectiveAf} />}
              {tab === 'tab-content' && <ContentTab af={effectiveAf} />}
              {tab === 'tab-chat' && tabAllowed('tab-chat', user?.role, chatOn) && (
                <ChatTab af={effectiveAf} />
              )}
              {tab === 'tab-links' && <ImportantLinksTab />}
              {!isReady(tab) && (
                <div className="tab-placeholder">
                  <h3>Not yet ported</h3>
                  <p>This tab will be ported next.</p>
                </div>
              )}
            </main>
          </>
        );
      }}
    />
  );
}
